import React, { FC, useEffect } from "react"

import {
    ActionIcon, Alert, Anchor,
    Collapse,
    Divider,
    Flex,
    Grid,
    Group,
    NumberInput,
    Popover,
    Radio,
    Select,
    Stack,
    Text
} from "@mantine/core"
import { TimeInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import {
    IconInfoCircle,
    IconMapPinOff,
    IconMinus,
    IconPlus,
    IconSun,
    IconSunrise,
    IconSunset
} from "@tabler/icons-react"
import { Dayjs } from "dayjs"

import { WittyPiScheduleLine } from "../../hooks/useParseSchedule"
import { useLanguage } from "../../../../contexts/language-context"
import { DeviceDocument } from "../../../../contexts/devices-context"

type OperatingTimeSettingLineProps = {
    value?: WittyPiScheduleLine
    onChange?: (value: WittyPiScheduleLine) => void
    times?: [Dayjs, Dayjs]
    loading?: boolean
    coordinates?: {latitude: number, longitude: number}
    device?: DeviceDocument
}

const OperatingTimeSettingLine: FC<OperatingTimeSettingLineProps> = ({ value, onChange, times, loading, coordinates, device }) => {
    const form = useForm<WittyPiScheduleLine>({
        initialValues: value,
    })
    const { L } = useLanguage()

    useEffect(() => onChange?.(form.values), [form.values])

    return (
        <Stack>
            <Radio.Group label={L("Determine when the device is active")} {...form.getInputProps("type")}>
                <Stack spacing="xs" mt="md">
                    <Radio value="interval" label={L("Between exact times")} />
                    <Collapse in={form.values.type === "interval"}>
                        <Grid ml="2rem" mb="md" gutter="xs">
                            <Grid.Col span={12} xs={6}>
                                <TimeInput label={L("Start at")} {...form.getInputProps("start")} />
                            </Grid.Col>
                            <Grid.Col span={12} xs={6}>
                                <TimeInput label={L("End at")} {...form.getInputProps("end")} />
                            </Grid.Col>
                        </Grid>
                    </Collapse>
                    <Radio
                        value={form.values.type !== "interval" ? form.values.type ?? "sunrise" : "sunrise"}
                        label={
                            <Group mt={-5} position="apart">
                                {L("Based on sunrise and sunset")}
                                <Popover width={300} shadow="md" withinPortal>
                                    <Popover.Target>
                                        <ActionIcon>
                                            <IconInfoCircle size={24} />
                                        </ActionIcon>
                                    </Popover.Target>
                                    <Popover.Dropdown>
                                        <Text size="sm">
                                            {L("The sunrise/sunset time shown are based on the coordinates of the device and are collected from %s.", <Anchor target="_blank" href="https://api.sunrise-sunset.org">api.sunrise-sunset.org</Anchor>)}
                                        </Text>
                                    </Popover.Dropdown>
                                </Popover>
                            </Group>
                        }
                        disabled={loading}
                    />
                    <Collapse
                        in={
                            form.values.type === "sunrise" ||
                            form.values.type === "sunset" ||
                            form.values.type === "both"
                        }
                    >
                        <Grid gutter="xs" ml="2rem" align="end">
                            <Grid.Col span={12} xs={4}>
                                <NumberInput
                                    label={L(
                                        "Before %s",
                                        form.values.type === "sunrise" || form.values.type === "both"
                                            ? L("sunrise")
                                            : L("sunset"),
                                    )}
                                    icon={<IconMinus size={16} />}
                                    min={0}
                                    step={5}
                                    {...form.getInputProps("preBuffer")}
                                />
                            </Grid.Col>
                            <Grid.Col span={12} xs={4}>
                                <Select
                                    data={[
                                        { label: L("Sunrise"), value: "sunrise" },
                                        { label: L("Sunset"), value: "sunset" },
                                        { label: L("Both"), value: "both" },
                                    ]}
                                    icon={
                                        form.values.type === "sunrise" ? (
                                            <IconSunrise size={18} />
                                        ) : form.values.type === "sunset" ? (
                                            <IconSunset size={18} />
                                        ) : (
                                            <IconSun size={18} />
                                        )
                                    }
                                    {...form.getInputProps("type")}
                                />
                            </Grid.Col>
                            <Grid.Col span={12} xs={4}>
                                <NumberInput
                                    label={L(
                                        "After %s",
                                        form.values.type === "sunrise" || form.values.type === "both"
                                            ? L("sunrise")
                                            : L("sunset"),
                                    )}
                                    icon={<IconPlus size={16} />}
                                    min={0}
                                    step={5}
                                    {...form.getInputProps("postBuffer")}
                                />
                            </Grid.Col>
                            {form.values.preBuffer !== undefined && form.values.postBuffer !== undefined && times && (
                                <Grid.Col span={12}>
                                    <Text color="dimmed">
                                        <Flex my="xs" justify="space-between" align="center" wrap="nowrap">
                                            {times[
                                                form.values.type === "sunrise"
                                                    ? 0
                                                    : form.values.type === "sunset"
                                                    ? 1
                                                    : 0
                                            ]
                                                .subtract(form.values.preBuffer, "m")
                                                .format("H:mm")}
                                            <Divider size="md" mx="xs" sx={{ flexGrow: 1 }} />
                                            {form.values.type === "sunrise" ? (
                                                <Group spacing="xs">
                                                    <IconSunrise size={20} />
                                                    {times[0].format("H:mm")}
                                                </Group>
                                            ) : form.values.type === "sunset" ? (
                                                <Group spacing="xs">
                                                    <IconSunset size={20} />
                                                    {times[1].format("H:mm")}
                                                </Group>
                                            ) : (
                                                <>
                                                    <Group spacing="xs">
                                                        <IconSunrise size={20} />
                                                        {times[0].format("H:mm")}
                                                    </Group>
                                                    <Divider size="md" mx="xs" sx={{ flexGrow: 3 }} />
                                                    <Group spacing="xs">
                                                        <IconSunset size={20} />
                                                        {times[1].format("H:mm")}
                                                    </Group>
                                                </>
                                            )}
                                            <Divider size="md" mx="xs" sx={{ flexGrow: 1 }} />
                                            {times[
                                                form.values.type === "sunrise"
                                                    ? 0
                                                    : form.values.type === "sunset"
                                                    ? 1
                                                    : 1
                                            ]
                                                .add(form.values.postBuffer, "m")
                                                .format("H:mm")}
                                        </Flex>
                                    </Text>
                                </Grid.Col>
                            )}
                        </Grid>
                    </Collapse>
                </Stack>
            </Radio.Group>
            {!coordinates && device ? (
                <Alert
                    title="No location specified"
                    color="light"
                    icon={<IconMapPinOff size={20} />}
                >
                    {L("Make sure a location is specified for this device to be able to preview sunset/sunrise times. Use the \'kebab menu\' (⋮) in the device details at the top of the page to set the location of the device.")}
                </Alert>
            ) : null}
        </Stack>
    )
}

export default OperatingTimeSettingLine
